import ExtendedPath from "csi/core/ExtendedPath";
import PathHints from "./PathHints";
import { ATTR_NAME, ATTR_TYPE, C8AttributeId, C8NodeType } from "src/c8-platform/type/node";
import { PATH_OP_CHILD, PATH_OP_INDEX, PATH_OP_PARENT, pathGetSteps } from "csi/utility/pathUtility";
import { arrayLast } from "csi/utility/arrayUtility";
import { isDefined } from "csi/utility/coreUtility";

const RE_START_TYPE = /^\((.+?)\)/;
const RE_STEP = /(.*?):(.+?)(?:\[(.*?)\])?\((.+?)\)/;

type ExtendedPathConstructor = new (
	startType: C8NodeType,
	pathHints?: PathHints,
	ignoreTypes?: boolean,
	steps?: ExtendedPathStep[],
) => ExtendedPath;

/** constructor for ExtendedPath class - must be registered here to avoid circular dependencies */
let extendedPathClass: ExtendedPathConstructor;

export interface ExtendedPathStep {
	/** attribute id */
	a: C8AttributeId;

	/** destination class of path step */
	c?: C8NodeType;

	/** index into container if step traverses multi-value attribute */
	key?: string;

	/** indicates if parent step; if not, then it is a child step */
	isParent?: boolean;
}

/**
 * Convert given path to a standard path
 * @throw {Error} throws Error if invalid path
 */
export function convertToStandardPath(path: string): string {
	return path ? parseExtendedPath(path, null, true).getStandardPath() : "";
}

/**
 * Return step object for given parameters
 */
export function createExtendedPathStep(attributeId: C8AttributeId, nodeType?: C8NodeType, key?: number | string, isParentStep?: boolean): ExtendedPathStep {
	const step: ExtendedPathStep = { a: attributeId };

	if (nodeType) {
		step.c = nodeType;
	}

	if (isDefined(key)) {
		step.key = key.toString();
	}

	if (isParentStep) {
		step.isParent = true;
	}

	return step;
}

/**
 * Parse given path into ExtendedPath
 * @param pathHints - hints to path traversal when model includes abstract classes or multiple parents to same class
 * @param ignoreTypes - if specified, allows standard paths to be parsed and target types will not be calculated
 * @throw {Error} throws Error if invalid path
 */
export function parseExtendedPath(path: string, pathHints?: PathHints, ignoreTypes?: boolean): ExtendedPath {
	console.assert(!!extendedPathClass);
	const m = RE_START_TYPE.exec(path);
	const startType = m?.[1];

	if (!startType) {
		if (!ignoreTypes) {
			throw new Error("Cannot find the start type in the extended path=" + path);
		}

		// convert standard steps to ExtendedPathStep objects
		const steps: ExtendedPathStep[] = [];

		pathGetSteps(path)?.forEach((step, i) => {
			const parsed = step.split(":");
			const op = parsed[0];

			if ((op == PATH_OP_CHILD) || (op == PATH_OP_PARENT)) {
				steps.push(createExtendedPathStep(parsed[1], null, null, (op == PATH_OP_PARENT)));
			} else if (i && (op == PATH_OP_INDEX)) {
				arrayLast(steps).key = parsed[1];
			} else {
				throw new Error("Invalid path: " + path);
			}
		});

		return new extendedPathClass(startType, pathHints, ignoreTypes, steps);
	}

	// create path with types
	const ep = new extendedPathClass(startType, pathHints, ignoreTypes);

	// convert extended path step string to ExtendedPathStep objects
	path = path.substring(startType.length + 2);

	if (path) {
		path.split("/").forEach((step) => {
			const [, stepType, attrId, index, targetType] = RE_STEP.exec(step);
			ep.addRelationStep(attrId, targetType, index, stepType == PATH_OP_PARENT);
		});
	}

	return ep;
}

/**
 * Parse given standard or extended path and return ExtendedPathSteps
 * @throw {Error} throws Error if invalid path
 */
export function parsePathSteps(path: string): ExtendedPathStep[] {
	return path ? parseExtendedPath(path, null, true).steps : [];
}

/**
 * Register ExtendedPath with service
 */
export function registerExtendedPathClass(ctor: ExtendedPathConstructor): void {
	extendedPathClass = ctor;
}

/** return extended path for attribute if it does not exist on the given paths abstract class */
export function getConcretePathForAttribute(id: C8AttributeId, extendedPath: ExtendedPath, onlyValidIfOne?: boolean): ExtendedPath | undefined {
	const { endClass } = extendedPath;

	if (endClass && (id !== ATTR_NAME) && (id !== ATTR_TYPE)) {
		// ensure that attribute is owned by path end class - if not, change end class to derived class that does own attribute;
		// in some cases a view will use Target="Node" if no class exists for elements in view, so ignore such cases when endClass is null
		const ra = endClass.getAttribute(id, true, false, true);

		if (!ra && !extendedPath.isOmitted) {
			// find attributes of any derived classes
			const matches = endClass.getConcreteClassesWithAttribute(id, true, (rc, ra) => {
				if (rc.disabled || !ra.isEnabled) {
					return false;
				}

				const ep = extendedPath.clone().setEndType(rc.type);
				return !ep.isOmitted;
			});

			const n = matches.length;

			if ((n === 1) || (!onlyValidIfOne && (n >= 1))) {
				// set path to reference first/only type matched
				extendedPath = extendedPath.clone().setEndType(matches[0].type);
			} else if (onlyValidIfOne) {
				// attribute not an exact match, so return no key when validating
				return;
			}
		}
	}

	return extendedPath;
}